import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import client from 'utils/client';
import config from 'config';
import { useAuth } from 'contexts/AuthContext';
import { useToast } from 'components/Toast/useToast';
import TextInput, { PasswordInput } from 'components/FormElements/TextInput';
import './profile.css';

const initialsOf = (first, last) => `${(first || '').charAt(0)}${(last || '').charAt(0)}`.toUpperCase() || '?';

function validatePassword(values) {
  const errors = {};
  if (!values.currentPassword) errors.currentPassword = 'Required';
  if (!values.newPassword) errors.newPassword = 'Required';
  if (values.newPassword && values.newPassword.length < 8) errors.newPassword = 'Minimum 8 characters';
  if (values.currentPassword && values.newPassword && values.currentPassword === values.newPassword) {
    errors.newPassword = 'Enter a different password';
  }
  if (values.newPassword !== values.confirmPassword) {
    errors.confirmPassword = 'Passwords must match';
  }
  return errors;
}

function Profile() {
  const { userDetails, setUserDetails, isAdmin, isMerchant } = useAuth();
  const { onOpen } = useToast();
  const [savingName, setSavingName] = useState(false);

  const id = userDetails?.id;
  const email = userDetails?.email || '';
  const permissions = userDetails?.permissions || [];

  const [merchantProfile, setMerchantProfile] = useState(null);
  React.useEffect(() => {
    if (!isMerchant) return;
    client.get('/merchants/me')
      .then((res) => setMerchantProfile(res.data.data))
      .catch((err) => onOpen({ type: 'error', message: err?.message || 'Failed to load profile' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMerchant]);

  const handleMerchantPasswordSubmit = (values, { resetForm }) => client
    .post('/authentication/change-password', {
      old_password: values.currentPassword,
      new_password: values.newPassword,
    })
    .then(() => {
      onOpen({ message: 'Password changed' });
      resetForm();
    })
    .catch((err) => onOpen({ type: 'error', message: err?.message || 'Password change failed' }));

  const handleNameSubmit = (values) => {
    setSavingName(true);
    return client
      .put(`/staff/${id}/update`, {
        firstName: values.first_name,
        lastName: values.last_name,
        active: userDetails?.active ?? true,
      })
      .then(() => {
        const next = { ...userDetails, first_name: values.first_name, last_name: values.last_name };
        setUserDetails(next);
        const stored = JSON.parse(sessionStorage.getItem(config.TOKEN)) || {};
        sessionStorage.setItem(config.TOKEN, JSON.stringify({ ...stored, first_name: values.first_name, last_name: values.last_name }));
        onOpen({ message: 'Profile updated' });
      })
      .catch((err) => onOpen({ type: 'error', message: err?.message || 'Update failed' }))
      .finally(() => setSavingName(false));
  };

  const handlePasswordSubmit = (values, { resetForm }) => client
    .post('/staff/reset/password', {
      email,
      oldPassword: values.currentPassword,
      newPassword: values.newPassword,
    })
    .then(() => {
      onOpen({ message: 'Password changed' });
      resetForm();
    })
    .catch((err) => onOpen({ type: 'error', message: err?.message || 'Password change failed' }));

  if (!userDetails) {
    return <div className='profile'><p>Not signed in.</p></div>;
  }

  if (isMerchant) {
    const owner = merchantProfile || {};
    const merchantBranches = owner.branches || userDetails.branches || [];
    return (
      <div className='profile'>
        <div className='profile-header'>
          <span className='profile-avatar' aria-hidden='true'>{initialsOf(owner.email || email, '')}</span>
          <div>
            <h1 className='profile-name'>{owner.email || email}</h1>
            <p className='profile-email'>{`${merchantBranches.length} branch${merchantBranches.length === 1 ? '' : 'es'}`}</p>
            <span className='profile-role-pill'>Merchant</span>
          </div>
        </div>

        <div className='form-wrapper'>
          <div className='form-section'>
            <div className='form-section-header'>
              <div className='form-section-title'>Branches</div>
              <div className='form-section-description'>All the locations this login can see. Contact support to update them.</div>
            </div>
            {merchantBranches.length === 0 ? (
              <p className='profile-permissions-empty'>No branches linked yet.</p>
            ) : (
              <ul className='profile-permissions'>
                {merchantBranches.map((b) => (
                  <li key={b.id} className='profile-permission-chip'>
                    {b.name || `#${b.id}`}
                    {b.account_number ? ` · ${b.account_number}` : ''}
                    {b.active === false ? ' · inactive' : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Formik
            initialValues={{ currentPassword: '', newPassword: '', confirmPassword: '' }}
            validate={validatePassword}
            onSubmit={handleMerchantPasswordSubmit}>
            {({ isSubmitting }) => (
              <Form>
                <div className='form-section'>
                  <div className='form-section-header'>
                    <div className='form-section-title'>Change password</div>
                    <div className='form-section-description'>Use at least 8 characters.</div>
                  </div>
                  <div className='form-grid'>
                    <PasswordInput className='form-field-md' name='currentPassword' label='Current password' required />
                    <PasswordInput className='form-field-md' name='newPassword' label='New password' required />
                    <PasswordInput className='form-field-md' name='confirmPassword' label='Confirm new password' required />
                  </div>
                  <div className='profile-actions'>
                    <button type='submit' className='btn-primary' disabled={isSubmitting}>
                      {isSubmitting ? 'Updating…' : 'Update password'}
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    );
  }

  return (
    <div className='profile'>
      <div className='profile-header'>
        <span className='profile-avatar' aria-hidden='true'>{initialsOf(userDetails.first_name, userDetails.last_name)}</span>
        <div>
          <h1 className='profile-name'>{`${userDetails.first_name || ''} ${userDetails.last_name || ''}`.trim() || 'Account'}</h1>
          <p className='profile-email'>{email}</p>
          <span className={`profile-role-pill ${isAdmin ? 'is-admin' : ''}`}>{isAdmin ? 'Admin' : 'Staff'}</span>
        </div>
      </div>

      <div className='form-wrapper'>
        <Formik
          initialValues={{ first_name: userDetails.first_name || '', last_name: userDetails.last_name || '' }}
          onSubmit={handleNameSubmit}
          enableReinitialize>
          {({ isSubmitting }) => (
            <Form>
              <div className='form-section'>
                <div className='form-section-header'>
                  <div className='form-section-title'>Personal details</div>
                  <div className='form-section-description'>Your name as it appears across the dashboard.</div>
                </div>
                <div className='form-grid'>
                  <TextInput className='form-field-md' name='first_name' label='First name' required />
                  <TextInput className='form-field-md' name='last_name' label='Last name' required />
                  <div className='form-field form-field-md'>
                    <label>Email</label>
                    <input type='text' value={email} disabled readOnly />
                  </div>
                </div>
                <div className='profile-actions'>
                  <button type='submit' className='btn-primary' disabled={isSubmitting || savingName}>
                    {isSubmitting || savingName ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>

        <div className='form-section'>
          <div className='form-section-header'>
            <div className='form-section-title'>Permissions</div>
            <div className='form-section-description'>
              {isAdmin
                ? 'You have full administrative access. Permissions can be adjusted from the Staff page.'
                : 'These determine what you can see and do. Contact an admin to request changes.'}
            </div>
          </div>
          {permissions.length === 0 ? (
            <p className='profile-permissions-empty'>No permissions assigned.</p>
          ) : (
            <ul className='profile-permissions'>
              {permissions.map((p) => (
                <li key={p} className='profile-permission-chip'>{p.replace(/_/g, ' ')}</li>
              ))}
            </ul>
          )}
        </div>

        <Formik
          initialValues={{ currentPassword: '', newPassword: '', confirmPassword: '' }}
          validate={validatePassword}
          onSubmit={handlePasswordSubmit}>
          {({ isSubmitting }) => (
            <Form>
              <div className='form-section'>
                <div className='form-section-header'>
                  <div className='form-section-title'>Change password</div>
                  <div className='form-section-description'>Use at least 8 characters. You stay signed in after changing.</div>
                </div>
                <div className='form-grid'>
                  <PasswordInput className='form-field-md' name='currentPassword' label='Current password' required />
                  <PasswordInput className='form-field-md' name='newPassword' label='New password' required />
                  <PasswordInput className='form-field-md' name='confirmPassword' label='Confirm new password' required />
                </div>
                <div className='profile-actions'>
                  <button type='submit' className='btn-primary' disabled={isSubmitting}>
                    {isSubmitting ? 'Updating…' : 'Update password'}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default Profile;
